import { Link } from 'react-router-dom';
import FurnitureNavbar from '../../../components/layout/Nav';
import './artisandashboard.scss';
import { useEffect, useState } from 'react';
import withAuth from '../../auth/WithAuth';
import { serverUrlAuth } from '../../../serverUrl';
import {
  FaHandshake,
  FaUserCircle,
  FaTrophy,
  FaProjectDiagram,
  FaCheckCircle,
  FaClock,
  FaCalendarAlt,
  FaChevronRight,
  FaFileAlt,
  FaBriefcase,
} from 'react-icons/fa';

const ArtisanDashboard = () => {
  const [artisanName, setArtisanName] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const name = localStorage.getItem('full_name');
    if (name) setArtisanName(name.split(' ')[0]);

    const fetchDashboardData = async () => {
      try {
        const response = await serverUrlAuth.get('projects/artisan-dashboard/');
        console.log('Artisan dashboard data:', response.data);
        setDashboardData(response.data);
      } catch (err) {
        console.error('Error fetching artisan dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <>
        <FurnitureNavbar />
        <div className="dashboard-loader">
          <div className="wooden-spinner"></div>
          <p>Crafting your experience...</p>
        </div>
      </>
    );
  }

  const data = dashboardData || {};

  return (
    <>
      <FurnitureNavbar />
      <div className="modern-dashboard artisan-dashboard">
        {/* Hero Section */}
        <section className="hero-craftsmanship">
          <div className="hero-pattern"></div>
          <div className="hero-content-modern">
            <div className="welcome-badge">
              <span className="badge-icon">🔨</span>
              <span>
                Welcome back,{' '}
                {artisanName || data.artisan_name?.split(' ')[0] || 'Artisan'}!
              </span>
            </div>
            <h1 className="hero-title">
              <span className="gradient-text">Your Craftsmanship</span>
              <span className="hero-accent">Builds Dreams</span>
            </h1>
            <p className="hero-subtitle">
              Showcase your skills, connect with clients, and bring beautiful
              furniture designs to life.
            </p>
            <div className="hero-actions">
              <Link
                to="/browse-project-requests"
                className="btn-primary-custom"
              >
                <FaBriefcase /> Browse Projects
              </Link>
              <Link to="/messaging" className="btn-secondary-custom">
                Messages
                {data.total_notifications > 0 && (
                  <span className="notification-dot">
                    {data.total_notifications}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </section>

        {/* Quick Stats Dashboard */}
        <div className="stats-dashboard">
          <div className="stat-card-modern">
            <div
              className="stat-icon-wrapper"
              style={{ background: '#67320020', color: '#673200' }}
            >
              <FaProjectDiagram />
            </div>
            <div className="stat-content">
              <h3>{data.open_projects || 0}</h3>
              <p>Open Projects</p>
            </div>
          </div>
          <div className="stat-card-modern">
            <div
              className="stat-icon-wrapper"
              style={{ background: '#4caf5020', color: '#4caf50' }}
            >
              <FaCheckCircle />
            </div>
            <div className="stat-content">
              <h3>{data.ongoing_projects_count || 0}</h3>
              <p>In Progress</p>
            </div>
          </div>
          <div className="stat-card-modern">
            <div
              className="stat-icon-wrapper"
              style={{ background: '#ff980020', color: '#ff9800' }}
            >
              <FaCheckCircle />
            </div>
            <div className="stat-content">
              <h3>{data.completed_projects_count || 0}</h3>
              <p>Completed</p>
            </div>
          </div>
          <div className="stat-card-modern">
            <div
              className="stat-icon-wrapper"
              style={{ background: '#9c27b020', color: '#9c27b0' }}
            >
              <FaFileAlt />
            </div>
            <div className="stat-content">
              <h3>{data.proposals_submitted || 0}</h3>
              <p>Proposals Sent</p>
            </div>
          </div>
        </div>

        {/* Ongoing Projects Grid Section */}
        {data.ongoing_projects && data.ongoing_projects.length > 0 && (
          <div className="projects-showcase">
            <div className="section-header-modern">
              <h2>
                <span className="header-icon">🎯</span>
                Your Ongoing Projects
                <span className="header-line"></span>
              </h2>
              <Link to="/my-projects" className="view-all-link">
                View All Projects <FaChevronRight />
              </Link>
            </div>
            <div className="projects-grid-modern">
              {data.ongoing_projects.map((project) => (
                <div key={project.id} className="project-card-modern">
                  <div className="project-header">
                    <div className="project-title-section">
                      <h3>{project.title}</h3>
                      <span className="project-artisan">
                        Client: {project.client_name}
                      </span>
                    </div>
                    <div className="project-budget">
                      KSh {(project.agreed_cost || 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="progress-modern">
                    <div className="progress-bar-modern">
                      <div
                        className="progress-fill-modern"
                        style={{ width: `${project.progress || 0}%` }}
                      ></div>
                    </div>
                    <div className="progress-stats">
                      <span>{project.progress || 0}% Complete</span>
                      <span>
                        Due{' '}
                        {project.deadline
                          ? new Date(project.deadline).toLocaleDateString()
                          : 'Not set'}
                      </span>
                    </div>
                  </div>
                  <div className="project-footer">
                    <div className="milestone-chip">
                      <span>
                        📅 Deadline:{' '}
                        {project.deadline
                          ? new Date(project.deadline).toLocaleDateString()
                          : 'Flexible'}
                      </span>
                    </div>
                    <Link
                      to={`/messaging?project=${project.id}`}
                      className="project-action"
                    >
                      View Updates →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="dashboard-grid-modern">
          {/* Left Column - Pending Requests (Projects to bid on) */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3>
                <FaBriefcase /> Projects Needing Artisans
              </h3>
              <Link to="/browse-project-requests" className="card-link">
                Browse all →
              </Link>
            </div>
            <div className="pending-requests-list">
              {data.pending_requests && data.pending_requests.length > 0 ? (
                data.pending_requests.slice(0, 5).map((request) => (
                  <div key={request.id} className="request-item">
                    <div className="request-header">
                      <h4>{request.title}</h4>
                      <div className="request-budget">
                        KSh {request.budget?.toLocaleString() || 0}
                      </div>
                    </div>
                    <div className="request-details">
                      <p className="request-description">
                        {request.description?.substring(0, 100)}...
                      </p>
                      <div className="request-meta">
                        <span>
                          <FaUserCircle /> {request.client}
                        </span>
                        <span>
                          <FaCalendarAlt /> Due:{' '}
                          {request.deadline
                            ? new Date(request.deadline).toLocaleDateString()
                            : 'Flexible'}
                        </span>
                      </div>
                    </div>
                    <div className="request-footer">
                      <Link
                        to={`/browse-project-requests?id=${request.id}`}
                        className="submit-proposal-btn"
                      >
                        Submit Proposal →
                      </Link>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-requests">
                  <p>No open projects available at the moment</p>
                  <p className="text-muted">
                    Check back later for new opportunities
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Active Negotiations */}
          <div className="dashboard-card negotiations-card">
            <div className="card-header">
              <h3>
                <FaHandshake /> Active Negotiations
              </h3>
              <Link to="/messages" className="card-link">
                View all →
              </Link>
            </div>
            <div className="negotiations-list">
              {data.active_negotiations &&
              data.active_negotiations.length > 0 ? (
                data.active_negotiations.map((negotiation) => (
                  <div key={negotiation.id} className="negotiation-item">
                    <div className="negotiation-header">
                      <h4>{negotiation.project_title}</h4>
                      {negotiation.unread_messages > 0 && (
                        <span className="unread-badge">
                          {negotiation.unread_messages} new
                        </span>
                      )}
                    </div>
                    <p className="client-name">
                      Client: {negotiation.client_name}
                    </p>
                    <div className="price-info">
                      <span>
                        Proposed: KSh{' '}
                        {negotiation.initial_price?.toLocaleString() || 0}
                      </span>
                      {negotiation.current_offer && (
                        <span className="counter-offer">
                          → KSh{' '}
                          {negotiation.current_offer?.toLocaleString() || 0}
                        </span>
                      )}
                    </div>
                    <div className="last-activity">
                      <FaClock /> Last: {negotiation.last_activity}
                    </div>
                    <Link
                      // to={`/messaging?proposal=${negotiation.id}`}
                      to={`/messaging`}
                      className="continue-btn"
                    >
                      Continue Discussion →
                    </Link>
                  </div>
                ))
              ) : (
                <div className="empty-negotiations">
                  <p>No active negotiations</p>
                  <Link to="/browse-project-requests" className="browse-link">
                    Browse projects to start negotiating
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity / Updates Section */}
        <div className="recent-activity-section">
          <div className="section-header-modern">
            <h2>
              <span className="header-icon">📋</span>
              Recent Activity
            </h2>
          </div>
          <div className="activity-grid">
            {/* Proposals Submitted */}
            {data.proposals_submitted > 0 && (
              <div className="activity-card">
                <div className="activity-icon">
                  <FaFileAlt />
                </div>
                <div className="activity-content">
                  <h4>Proposals Submitted</h4>
                  <p>
                    You have submitted {data.proposals_submitted} proposal
                    {data.proposals_submitted !== 1 ? 's' : ''}
                  </p>
                  <Link to="/my-proposals" className="activity-link">
                    View your proposals →
                  </Link>
                </div>
              </div>
            )}

            {/* Ongoing Projects Update */}
            {data.ongoing_projects_count > 0 && (
              <div className="activity-card">
                <div className="activity-icon">
                  <FaProjectDiagram />
                </div>
                <div className="activity-content">
                  <h4>Projects in Progress</h4>
                  <p>
                    You are currently working on {data.ongoing_projects_count}{' '}
                    project{data.ongoing_projects_count !== 1 ? 's' : ''}
                  </p>
                  <Link to="/my-projects" className="activity-link">
                    View your projects →
                  </Link>
                </div>
              </div>
            )}

            {/* Completed Projects */}
            {data.completed_projects_count > 0 && (
              <div className="activity-card">
                <div className="activity-icon">
                  <FaTrophy />
                </div>
                <div className="activity-content">
                  <h4>Completed Projects</h4>
                  <p>
                    You have successfully completed{' '}
                    {data.completed_projects_count} project
                    {data.completed_projects_count !== 1 ? 's' : ''}
                  </p>
                  <Link to="/completed-projects" className="activity-link">
                    View completed →
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tips & Insights for Artisans */}
        <div className="tips-section">
          <div className="tips-card">
            <div className="tips-icon">💡</div>
            <div className="tips-content">
              <h4>Pro Tips for Success</h4>
              <ul>
                <li>
                  ✓ Respond to messages quickly to build trust with clients
                </li>
                <li>
                  ✓ Submit detailed proposals with clear pricing and timelines
                </li>
                <li>
                  ✓ Share progress photos regularly to keep clients engaged
                </li>
                <li>✓ Build your portfolio to showcase your best work</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withAuth(ArtisanDashboard, 'artisan');
